/**
 * Who teaches what.
 *
 * The built-in CAPS material has no author, so somebody has to say which
 * instructor takes it — that assignment is what gives a learner a teacher and
 * an instructor a class.
 *
 * It used to claim each curriculum had "one exclusive owner". That stopped
 * being true when two people were allowed to teach one subject: the page read
 * the first teacher and hid the rest, so an assignment an admin could not see
 * was one they could not undo.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../../api/client";
import type { User } from "../../types";

type AdminCurriculum = {
  id: string;
  title: string;
  teachers: { id: string; name: string }[];
  /** Who wrote it — "PyDialog" for the platform's own material. */
  publisher: string;
  /** True when an instructor authored it, in which case it is theirs. */
  authored: boolean;
};

export function AdminCurriculaPage() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState<Record<string, string>>({});
  const [query, setQuery] = useState("");

  const { data: curricula, isLoading } = useQuery({
    queryKey: ["admin-curricula"],
    queryFn: () => apiGet<AdminCurriculum[]>("/api/admin/curricula"),
  });

  const { data: users } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => apiGet<User[]>("/api/admin/users"),
  });

  // Only approved instructors: assigning a subject to someone who cannot sign
  // in yet puts their name on a course nobody is actually teaching.
  const instructors =
    users?.filter((u) => u.role === "instructor" && u.is_approved) ?? [];

  const change = useMutation({
    mutationFn: (data: {
      curriculum_id: string;
      instructor_id: string;
      remove?: boolean;
    }) => apiPost("/api/admin/curricula/assign", data),
    onSuccess: (_d, { curriculum_id }) => {
      setAdding((a) => {
        const next = { ...a };
        delete next[curriculum_id];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["admin-curricula"] });
      // The catalogue's "taught by" line reads the same data.
      queryClient.invalidateQueries({ queryKey: ["curricula"] });
      queryClient.invalidateQueries({ queryKey: ["available-subjects"] });
    },
  });

  if (isLoading) return <p className="text-muted">Loading curricula...</p>;

  // Seventeen rows is already too many to scan when looking for the one
  // subject a teacher just asked about.
  const needle = query.trim().toLowerCase();
  const shown = (curricula ?? []).filter(
    (c) =>
      !needle ||
      [c.title, c.id, c.publisher]
        .concat(c.teachers.map((t) => t.name))
        .some((f) => f?.toLowerCase().includes(needle)),
  );

  return (
    <div className="admin-curricula">
      <h1>Who teaches what</h1>
      <p className="text-muted">
        Assign an instructor to a subject to give them a class in it. Several
        can teach the same subject — a learner belongs to the one they chose
        when enrolling. This does not change who wrote the material.
      </p>

      <div className="card user-filter">
        <label>
          <span className="field-label">Find a subject</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Subject, id, publisher or teacher"
          />
        </label>
      </div>

      <div className="card">
        {shown.length === 0 ? (
          <p className="text-muted" style={{ margin: 0 }}>
            No subject matches “{query}”.
          </p>
        ) : (
        <table>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Published by</th>
              <th>Teachers</th>
              <th>Add a teacher</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => {
              const taken = new Set(c.teachers.map((t) => t.id));
              const available = instructors.filter((i) => !taken.has(i.id));
              return (
                <tr key={c.id}>
                  <td>
                    <strong>{c.title}</strong>
                    <br />
                    <span className="text-muted">{c.id}</span>
                  </td>
                  <td>
                    {c.publisher}
                    {c.authored && (
                      <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                        instructor's own course
                      </div>
                    )}
                  </td>
                  <td>
                    {c.teachers.length === 0 ? (
                      <span className="text-muted">Nobody yet</span>
                    ) : (
                      <ul className="teacher-chips">
                        {c.teachers.map((t) => (
                          <li key={t.id}>
                            {t.name}
                            <button
                              type="button"
                              aria-label={`Remove ${t.name} from ${c.title}`}
                              title={`Remove ${t.name}`}
                              disabled={change.isPending}
                              onClick={() =>
                                change.mutate({
                                  curriculum_id: c.id,
                                  instructor_id: t.id,
                                  remove: true,
                                })
                              }
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td>
                    {available.length === 0 ? (
                      <span className="text-muted">
                        {instructors.length === 0
                          ? "No approved instructors"
                          : "All of them already teach it"}
                      </span>
                    ) : (
                      <>
                        <select
                          value={adding[c.id] ?? ""}
                          className="inline-select"
                          onChange={(e) =>
                            setAdding((a) => ({ ...a, [c.id]: e.target.value }))
                          }
                        >
                          <option value="">— Choose —</option>
                          {available.map((i) => (
                            <option key={i.id} value={i.id}>
                              {i.name}
                            </option>
                          ))}
                        </select>{" "}
                        {adding[c.id] && (
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={change.isPending}
                            onClick={() =>
                              change.mutate({
                                curriculum_id: c.id,
                                instructor_id: adding[c.id],
                              })
                            }
                          >
                            Add
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
